import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
    ButtonRenderProps,
    ButtonProps,
    DetailsRowProps,
    ContentSection,
    Grid,
    GridRow,
    GridColumn,
    SearchBox,
    TableFilter,
    Checkbox,
    Stack,
    ClearFiltersButton,
    ListTable,
    RenderFunction,
    DropdownMultiselect
} from "sprint-common-web";

import { TagElementsList } from "../../../common/components/TagElementsList";
import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { renderNotMergableRow } from "../../common/utils/renderNotMergableRow";
import { contentManageRoutes } from "../../ContentRoutes";
import { CreateTvSeriesButton, CreateTvSeriesButtonProps } from "./buttons/CreateTvSeriesButton";
import { MergeTvSeriesCommandBarButton } from "./buttons/MergeTvSeriesCommandBarButton";
import { createTvSeriesLisitngColumns } from "./createTvSeriesLisitngColumns";
import { TvSeriesListingItem } from "./TvSeriesListingItem";
import { useTvSeriesListing } from "./useTvSeriesListing";
import { sourceOptions, useTvSeriesListingFilters } from "./useTvSeriesListingFilters";

export const TvSeriesListing: FunctionComponent = () => {
    const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
    const {
        filters,
        setSort,
        setPage,
        setPageSize,
        setTitle,
        clearFilters,
        setIncludeMerged,
        setSources
    } = useTvSeriesListingFilters();
    const {
        items,
        totalItems,
        isListLoading,
        tvSeriesToMerge,
        getData,
        updateListAfterMerge,
        addNewItemToList,
        clearTvSeriesToMerge,
        addTvSeriesItemToMergeList,
        removeTvSeriesFromMergeList
    } = useTvSeriesListing();
    const columns = useMemo(
        () =>
            createTvSeriesLisitngColumns({
                tvSeriesToMerge,
                addTvSeriesItemToMergeList,
                tvSeriesListingItems: items
            }),
        [tvSeriesToMerge, items, addTvSeriesItemToMergeList]
    );

    useEffect(() => {
        if (!isListLoading) {
            setIsPageLoading(false);
        }
    }, [isListLoading]);

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const handleIncludeMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked ?? false);
    };

    const handleTvSeriesCreated = (newTvSeries: TvSeriesListingItem) => {
        addNewItemToList(newTvSeries, filters);
    };

    const handleUpdateListAfterMerge = (sourceTvSeries: TvSeriesListingItem[], newTvSeries: TvSeriesListingItem) => {
        updateListAfterMerge(sourceTvSeries, newTvSeries, filters);
    };

    const createTvSeriesButtonProps: ButtonRenderProps<CreateTvSeriesButtonProps> = {
        render: () => CreateTvSeriesButton({ onTvSeriesCreated: handleTvSeriesCreated, isLabeled: true })
    };

    const mergeTvSeriesButtonProps: ButtonRenderProps<ButtonProps> = {
        render: () =>
            MergeTvSeriesCommandBarButton({
                tvSeriesToMerge,
                clearTvSeriesToMerge,
                updateListAfterMerge: handleUpdateListAfterMerge
            })
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(tvSeriesToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={contentManageRoutes} />
            </PageHeader>
            <ContentSection
                isLoading={isPageLoading}
                header="TV Series"
                buttons={[mergeTvSeriesButtonProps, createTvSeriesButtonProps]}
            >
                <Grid>
                    <GridRow>
                        <TagElementsList
                            items={tvSeriesToMerge}
                            onItemDismiss={removeTvSeriesFromMergeList}
                            renderItemText={renderMovieText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={4}>
                            <SearchBox placeholder="Title" onChange={setTitle} value={filters.title} />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <DropdownMultiselect
                                    testId="tv-series-filters-sources"
                                    placeholder="Source"
                                    selectedKeys={filters.sources}
                                    onChange={setSources}
                                    options={sourceOptions}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <TableFilter>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleIncludeMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </TableFilter>
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={items}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isListLoading}
                                onRenderRow={tvSeriesToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const renderMovieText = (movie: TvSeriesListingItem) => {
    return movie.title;
};

const isRowMergable = (tvSeriesToMerge: TvSeriesListingItem[], rowItem: TvSeriesListingItem) => {
    const movieToMerge = tvSeriesToMerge[0];

    return (
        (tvSeriesToMerge.some((movie: TvSeriesListingItem) => movie.id === rowItem.id) ||
            (tvSeriesToMerge.length < 2 && onlyOneMovieHasBuzzId(movieToMerge, rowItem))) &&
        !rowItem.merged
    );
};

const onlyOneMovieHasBuzzId = (movie1: TvSeriesListingItem, movie2: TvSeriesListingItem) => {
    return !movie1.buzzId !== !movie2.buzzId;
};
